import { useCallback, useRef } from 'react';
import { getWorkspace } from '@utils/store';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { useCurrentRoute } from '@hooks/useCurrentRoute';
import { useStateCleanup } from '@hooks/useStateCleanup';
import { useWorkspaceNavigate } from '@hooks/useWorkspaceNavigate';
import { SelectedFilters } from '@constants/data-table';
import { PipelineDesiredStatusCodes } from '@constants/pipelines';
import { addNotification } from '@redux/actions/notificationActions';
import { NotificationType } from '@redux/types/types';
import { LimitCategory } from '@modules/Upgrade/consts/pricing';
import useFreeLimits from '@modules/Upgrade/hooks/useFreeLimits';
import {
  IndexDetailsConnectedPipelinesSource,
  IndexDetailsTab,
  IndexesListTab,
  IndexMenuAction,
  LIMIT_DEPLOYED_PIPELINES_CONNECTED,
  UPGRADE_POPOVER_INDEX_CONTENT,
} from '../constants/indexes';

import {
  deleteIndex as deleteIndexAction,
  disableIndex as disableIndexAction,
  downloadIndexDocuments as downloadIndexDocumentsAction,
  enableIndex as enableIndexAction,
  fetchConnectedPipelines as fetchConnectedPipelinesAction,
  fetchIndex as fetchIndexAction,
  fetchIndexes as fetchIndexesAction,
  FetchIndexProps,
  fetchIndexTotalDocuments as fetchIndexTotalDocumentsAction,
  POLLING_KEYS,
  selectActiveIndexDetailsTab,
  selectActiveIndexesListTab,
  selectConnectedPipelinesCard,
  selectConnectedPipelinesModal,
  selectCreateIndexStatus,
  selectDeleteIndexStatus,
  selectDisableIndexStatus,
  selectDuplicateIndexStatus,
  selectEnableIndexStatus,
  selectFetchConnectedPipelinesStatus,
  selectFetchIndexesStatus,
  selectFetchIndexStatus,
  selectFetchIndexTotalDocumentsStatus,
  selectHasDeployedPipelinesConnected,
  selectIndex,
  selectIndexes,
  selectIndexStats,
  selectIsConnectedPipelinesModalOpen,
  selectIsCreateIndexModalOpen,
  selectMessage,
  selectSortValueIndexesList,
  selectUpdateIndexStatus,
  selectUpdateIndexYamlStatus,
  setActiveIndexDetailsTab as setActiveIndexDetailsTabAction,
  setActiveIndexesListTab,
  setHasDeployedPipelinesConnected as setHasDeployedPipelinesConnectedAction,
  setIndexYaml as setIndexYamlAction,
  setIsConnectedPipelinesModalOpen as setIsConnectedPipelinesModalOpenAction,
  setIsCreateIndexModalOpen as setIsCreateIndexModalOpenAction,
  startPollingIndex as startPollingIndexAction,
  startPollingIndexes as startPollingIndexesAction,
  stopPolling as stopPollingAction,
  updateIndex as updateIndexAction,
  updateIndexYaml as updateIndexYamlAction,
} from '../redux/indexesSlice';
import { IPipelineIndex } from '../types/pipeline-index';

const useIndexes = () => {
  const dispatch = useAppDispatch();
  const workspaceNavigate = useWorkspaceNavigate();
  const { setRouteName } = useCurrentRoute();
  const { isLimitedUser, isLimitExceed } = useFreeLimits();

  const indexes = useAppSelector(selectIndexes);
  const index = useAppSelector(selectIndex);
  const message = useAppSelector(selectMessage);
  const fetchIndexesStatus = useAppSelector(selectFetchIndexesStatus);
  const fetchIndexStatus = useAppSelector(selectFetchIndexStatus);
  const activeIndexesListTab = useAppSelector(selectActiveIndexesListTab);
  const activeIndexDetailsTab = useAppSelector(selectActiveIndexDetailsTab);
  const sortValueIndexesList = useAppSelector(selectSortValueIndexesList);
  const indexStats = useAppSelector(selectIndexStats);
  const fetchIndexTotalDocumentsStatus = useAppSelector(selectFetchIndexTotalDocumentsStatus);
  const connectedPipelinesCard = useAppSelector(selectConnectedPipelinesCard);
  const connectedPipelinesModal = useAppSelector(selectConnectedPipelinesModal);
  const isConnectedPipelinesModalOpen = useAppSelector(selectIsConnectedPipelinesModalOpen);
  const hasDeployedPipelinesConnected = useAppSelector(selectHasDeployedPipelinesConnected);
  const fetchConnectedPipelinesStatus = useAppSelector(selectFetchConnectedPipelinesStatus);
  const updateIndexStatus = useAppSelector(selectUpdateIndexStatus);
  const updateIndexYamlStatus = useAppSelector(selectUpdateIndexYamlStatus);
  const enableIndexStatus = useAppSelector(selectEnableIndexStatus);
  const disableIndexStatus = useAppSelector(selectDisableIndexStatus);
  const deleteIndexStatus = useAppSelector(selectDeleteIndexStatus);
  const duplicateIndexStatus = useAppSelector(selectDuplicateIndexStatus);
  const createIndexStatus = useAppSelector(selectCreateIndexStatus);
  const isCreateIndexModalOpen = useAppSelector(selectIsCreateIndexModalOpen);

  const { reset } = useStateCleanup();

  const indexesDataTableParams = useRef<{
    pageNumber?: number;
    limit?: number;
    searchValue?: string;
    sortValue?: string;
    desiredStatus?: PipelineDesiredStatusCodes;
    filterValues?: SelectedFilters;
  } | null>(null);

  const getDesiredStatusFromActiveTab = () => {
    const mapDesiredStatusFromActiveTab = {
      [IndexesListTab.ALL]: undefined,
      [IndexesListTab.ACTIVE]: PipelineDesiredStatusCodes.DEPLOYED,
      [IndexesListTab.DRAFTS]: PipelineDesiredStatusCodes.UNDEPLOYED,
    };

    return mapDesiredStatusFromActiveTab[activeIndexesListTab];
  };

  const fetchIndexes = ({
    limit,
    pageNumber,
    desiredStatus,
    searchValue,
    sortValue,
    filterValues,
  }: {
    limit?: number;
    pageNumber?: number;
    desiredStatus?: PipelineDesiredStatusCodes;
    searchValue?: string;
    sortValue?: string;
    filterValues?: SelectedFilters;
  }) => {
    const desiredStatusLocal = desiredStatus || getDesiredStatusFromActiveTab();
    const params = {
      limit,
      pageNumber,
      searchValue,
      sortValue,
      filterValues,
      desiredStatus: desiredStatusLocal,
    };
    dispatch(fetchIndexesAction(params));

    indexesDataTableParams.current = {
      ...params,
    };
  };

  const startPollingIndexes = ({
    pageNumber,
    limit,
    searchValue,
    sortValue,
    desiredStatus,
  }: {
    pageNumber?: number;
    limit?: number;
    searchValue?: string;
    sortValue?: string;
    desiredStatus?: PipelineDesiredStatusCodes;
  }) => {
    dispatch(
      startPollingIndexesAction({
        pageNumber,
        limit,
        searchValue,
        sortValue,
        desiredStatus,
      }),
    );
  };

  const stopPollingIndexes = () => {
    dispatch(stopPollingAction(POLLING_KEYS.INDEXES));
  };

  const fetchConnectedPipelines = (params: {
    indexId: string;
    limit?: number;
    pageNumber?: number;
    source?: IndexDetailsConnectedPipelinesSource;
    desiredStatus?: PipelineDesiredStatusCodes;
  }) => {
    dispatch(fetchConnectedPipelinesAction(params));
  };

  const checkDeployedPipelinesConnected = (indexId: string) => {
    fetchConnectedPipelines({
      indexId,
      limit: LIMIT_DEPLOYED_PIPELINES_CONNECTED,
      source: IndexDetailsConnectedPipelinesSource.CHECK_DEPLOYED_PIPELINES,
      desiredStatus: PipelineDesiredStatusCodes.DEPLOYED,
    });
  };

  const fetchIndex = useCallback(
    (props: FetchIndexProps) => {
      dispatch(fetchIndexAction(props));
    },
    [dispatch],
  );

  const startPollingIndex = useCallback(
    (indexName: string) => {
      dispatch(startPollingIndexAction(indexName));
    },
    [dispatch],
  );

  const stopPollingIndex = useCallback(() => {
    dispatch(stopPollingAction(POLLING_KEYS.INDEX_DETAILS));
  }, [dispatch]);

  const updateIndex = (indexName: string, payload: Partial<IPipelineIndex>) => {
    dispatch(updateIndexAction({ indexName, payload }));
  };

  const setIndexYaml = (yaml: string) => {
    dispatch(setIndexYamlAction({ yaml }));
  };

  const updateIndexYaml = (indexName: string, yaml: string) => {
    dispatch(updateIndexYamlAction({ indexName, yaml }));
  };

  const enableIndex = (indexName: string) => {
    dispatch(enableIndexAction(indexName));
  };

  const disableIndex = (indexName: string) => {
    dispatch(disableIndexAction(indexName));
  };

  const fetchIndexTotalDocuments = (indexName: string) => {
    dispatch(fetchIndexTotalDocumentsAction(indexName));
  };

  const setIsCreateIndexModalOpen = (isOpen: boolean) => {
    dispatch(setIsCreateIndexModalOpenAction(isOpen));
  };

  const onChangeIndexesListTab = (tab: IndexesListTab) => {
    dispatch(setActiveIndexesListTab(tab));
  };

  const onCreateNewIndexButtonClick = () => {
    setIsCreateIndexModalOpen(true);
  };

  const onChangeIndexDetailsTab = useCallback(
    (tab: IndexDetailsTab) => {
      dispatch(setActiveIndexDetailsTabAction(tab));
    },
    [dispatch],
  );

  const setHasDeployedPipelinesConnected = (newHasDeployedPipelinesConnected: boolean) => {
    dispatch(setHasDeployedPipelinesConnectedAction(newHasDeployedPipelinesConnected));
  };

  const onRenameIndex = (indexName: string, newPipelineName: string) => {
    const payload = { name: newPipelineName };
    updateIndex(indexName, payload);
  };

  const setIsConnectedPipelinesModalOpen = (isOpen: boolean) => {
    dispatch(setIsConnectedPipelinesModalOpenAction(isOpen));
  };

  const setIndexDetailsRoute = (indexName: string) => {
    setRouteName(indexName);
    const workspaceName = getWorkspace();
    const newUrl = `/${workspaceName}/indexes/${indexName}`;
    window.history.replaceState(null, '', newUrl);
  };

  const deleteIndex = (indexName: string) => {
    dispatch(deleteIndexAction(indexName));
  };

  const getIndexDetailsPageRoute = (indexName: string): string => {
    return `/indexes/${indexName}`;
  };

  const getIndexStudioPageRoute = (indexName: string): string => {
    return `/indexes/${indexName}/builder`;
  };

  const duplicateIndex = (indexName: string) => {
    if (isLimitedUser && isLimitExceed(LimitCategory.INDEXES)) {
      dispatch(
        addNotification({
          content: UPGRADE_POPOVER_INDEX_CONTENT,
          type: NotificationType.Warning,
        }),
      );
      return;
    }

    workspaceNavigate(`/indexes/builder?template=${indexName}`);
  };

  const downloadIndexDocuments = (indexName: string) => {
    dispatch(downloadIndexDocumentsAction(indexName));
  };

  const handleListMoreActionClick = async (key: string, indexName: string) => {
    if (key === IndexMenuAction.Edit) workspaceNavigate(`/indexes/${indexName}/builder`);
    if (key === IndexMenuAction.Enable) enableIndex(indexName);
    if (key === IndexMenuAction.Disable) disableIndex(indexName);
    if (key === IndexMenuAction.Delete) deleteIndex(indexName);
    if (key === IndexMenuAction.Duplicate) duplicateIndex(indexName);
    if (key === IndexMenuAction.Download) downloadIndexDocuments(indexName);
  };

  // Rest handlers

  const resetIndexDetailsState = useCallback(() => {
    reset([
      'indexesStore.index',
      'indexesStore.indexLogs',
      'indexesStore.fetchConnectedPipelinesStatus',
      'indexesStore.connectedPipelinesCard',
      'indexesStore.connectedPipelinesModal',
      'indexesStore.fetchIndexStatus',
      'indexesStore.updateIndexStatus',
      'indexesStore.deleteIndexStatus',
      'indexesStore.fetchIndexTotalDocumentsStatus',
      'indexesStore.indexLogsDownloadStatus',
      'indexesStore.fetchIndexLogsStatus',
    ]);
  }, [reset]);

  const resetUpdateIndexStatus = () => {
    reset(['indexesStore.message', 'indexesStore.updateIndexStatus']);
  };

  const resetDeleteIndexStatus = () => {
    reset(['indexesStore.deleteIndexStatus']);
  };

  return {
    indexesDataTableParams,
    indexes,
    fetchIndexesStatus,
    activeIndexesListTab,
    index,
    fetchIndexStatus,
    isCreateIndexModalOpen,
    indexStats,
    fetchIndexTotalDocumentsStatus,
    connectedPipelinesCard,
    connectedPipelinesModal,
    fetchConnectedPipelinesStatus,
    sortValueIndexesList,
    activeIndexDetailsTab,
    message,
    createIndexStatus,
    onCreateNewIndexButtonClick,
    onChangeIndexesListTab,
    fetchIndexes,
    fetchIndex,
    startPollingIndex,
    stopPollingIndex,
    startPollingIndexes,
    stopPollingIndexes,
    fetchConnectedPipelines,
    setIsCreateIndexModalOpen,
    fetchIndexTotalDocuments,
    onChangeIndexDetailsTab,
    handleListMoreActionClick,
    updateIndex,
    setIndexYaml,
    getIndexStudioPageRoute,
    getIndexDetailsPageRoute,
    updateIndexStatus,
    updateIndexYamlStatus,
    resetUpdateIndexStatus,
    updateIndexYaml,
    onRenameIndex,
    enableIndex,
    disableIndex,
    deleteIndex,
    duplicateIndex,
    duplicateIndexStatus,
    deleteIndexStatus,
    enableIndexStatus,
    disableIndexStatus,
    setIndexDetailsRoute,
    resetDeleteIndexStatus,
    resetIndexDetailsState,
    isConnectedPipelinesModalOpen,
    setIsConnectedPipelinesModalOpen,
    hasDeployedPipelinesConnected,
    checkDeployedPipelinesConnected,
    setHasDeployedPipelinesConnected,
  };
};

export default useIndexes;
